import { Injectable } from "@angular/core";
import { last } from "rxjs";
import { Author, ItemWithRelations } from "src/app/api/models";

@Injectable({
  providedIn: "root",
})
export class AuthorService {
  NO_AUTHOR = { cs: "Bez autora", en: "No author" };
  MORE_AUTHORS = { cs: "Více autorů", en: "More authors" };

  constructor() {}

  /**
   * Parses an Author object to generate a formatted author name.
   * It prioritizes the nickname if available; otherwise, it falls back to combining
   * the firstname and lastname if both are available, or returns whichever is available.
   *
   * @param author - The Author object.
   * @returns A formatted string representing the author's name.
   */
  parseAuthorName(author: Author): string {
    let authorName = "";

    // Use nickname if it exists.
    if (author.nickname) {
      authorName = author.nickname;
    }
    // If both firstname and lastname are available, combine them.
    else if (author.firstname && author.lastname) {
      authorName = `${author.firstname} ${author.lastname}`;
    }
    // If only firstname is available, use it.
    else if (author.firstname) {
      authorName = author.firstname;
    }
    // If only lastname is available, use it.
    else if (author.lastname) {
      authorName = author.lastname;
    }

    return authorName;
  }

  /**
   * Generates a sort-friendly string from an Author object.
   *
   * The function evaluates the available name properties on the author:
   * - If both firstname and lastname are missing, the nickname is returned.
   * - If either firstname or lastname exists, it compares the nickname
   *   with the concatenated result of firstname and lastname (adding a space
   *   between them if both are present). If they differ, the nickname is preferred.
   * - Otherwise, if the nickname matches the concatenated name or is absent,
   *   the lastname is used (or the nickname if lastname is also absent).
   *
   * @param author - The optional Author object.
   * @returns A string for sorting the author, or null if no author is provided.
   */
  getAuthorStringForSort(author?: Author): string | null {
    let authorString = null;

    // Return null if there's no author provided.
    if (!author) {
      return authorString;
    }

    const { firstname, lastname, nickname } = author;

    // If both firstname and lastname are missing, use the nickname.
    if (!firstname && !lastname) {
      authorString = nickname;
    }
    // If at least one of firstname or lastname exists, and the nickname does not
    // equal the combination of firstname and lastname, then use the nickname.
    else if (
      (firstname || lastname) &&
      nickname !== firstname + (firstname && lastname ? " " : "") + lastname
    ) {
      authorString = nickname;
    }
    // Otherwise, if the nickname equals the full name or is not distinct,
    // use the lastname for sorting. If lastname is missing, fallback to using the nickname.
    else {
      authorString = lastname ? lastname : nickname;
    }

    return authorString;
  }
}
